import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import ModalContext from 'contexts/ModalContext'
import { EmptyArray, fillArray, take, unique } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import switchFunc from 'lib/function/switchFunc'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import LineBedBunkIcon from 'LuxKit/Icons/line/LineBedBunkIcon'
import LineBedSingleIcon from 'LuxKit/Icons/line/LineBedSingleIcon'
import LineBedQueenIcon from 'LuxKit/Icons/line/LineBedQueenIcon'
import LineBedDoubleAltIcon from 'LuxKit/Icons/line/LineBedDoubleAltIcon'
import LineBedKingIcon from 'LuxKit/Icons/line/LineBedKingIcon'
import LineBedSofaIcon from 'LuxKit/Icons/line/LineBedSofaIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import VillasPageAmenitiesModalContent from './VillasPageAmenitiesModalContent'
import VillasPageAmenity from './VillasPageAmenity'
import Modal from 'LuxKit/Modal/Modal'
import moment from 'moment'
import { inclusionTravelPeriodFilter } from 'lib/offer/inclusionsTravelPeriodFilter'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: repeat(2, minmax(0, 280px));
  grid-gap: ${rem(16)};
`

const BedroomGrid = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template-columns: 1fr 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

const ServiceItem = styled.div`
  padding: ${rem(16)} 0;
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`

const HighlightTile = styled(VerticalSpacer)`
  width: ${rem(200)};

  ${mediaQueryUp.desktop} {
    width: auto;
  }

  > picture {
    max-height: ${rem(180)};
  }
`

const BedroomTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: ${rem(160)};
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

const bedroomTypeToIcon = switchFunc<React.ReactNode, App.VillaRoomBedType>({
  QUEEN_SIZE_BED: <LineBedQueenIcon size="XL" />,
  SINGLE_BED: <LineBedSingleIcon size="XL" />,
  BUNK_BED: <LineBedBunkIcon size="XL" />,
  DOUBLE_BED: <LineBedDoubleAltIcon size="XL" />,
  KING_SIZE_BED: <LineBedKingIcon size="XL" />,
  SOFABED: <LineBedSofaIcon size="XL" />,
  DOUBLE_SOFA_BED: <LineBedSofaIcon size="XL" />,
  TRIBUNK_BED: <LineBedBunkIcon size="XL" />,
  BUNK_BEDS_SGL_ON_TOP_DBL_ON_BOTTOM: <LineBedBunkIcon size="XL" />,
}, <LineBedIcon size="XL" />)

interface Props {
  offer: App.VillaOffer;
  selectedDuration: number | null;
  checkInDate?: string;
}

function VillasPageRoomsAndAmenities(props: Props) {
  const {
    offer,
    selectedDuration,
    checkInDate,
  } = props

  const showModal = useContext(ModalContext)
  const amenities = offer.packages[0]?.roomType.amenities ?? EmptyArray
  const openAmenitiesModal = useCallback(() => {
    showModal(
      <Modal title="Amenities">
        <VillasPageAmenitiesModalContent amenities={amenities} />
      </Modal>,
    )
  }, [showModal, amenities])

  const roomsWithBeds = useMemo(() => offer.property.rooms.filter(room => room.beds.length > 0), [offer])

  const vipServices = useMemo(() => {
    // When no duration is selected, we show all inclusions
    const packages = offer.packages.filter(pkg => selectedDuration ? pkg.duration === selectedDuration : true)
    const allInclusions = packages.flatMap(pkg => pkg.inclusions ?? [])

    const checkOutDate = moment(checkInDate).add(selectedDuration, 'days').format(ISO_DATE_FORMAT)
    const filteredInclusions = checkInDate ? inclusionTravelPeriodFilter(allInclusions, checkInDate, checkOutDate) : allInclusions

    const allServices = unique(filteredInclusions.map(inclusion => inclusion.description))
    return allServices.map(inclusionDescription => inclusionDescription?.startsWith('-') ?
        inclusionDescription?.slice(1).trim() :
      inclusionDescription,
    )
  }, [checkInDate, offer.packages, selectedDuration])

  return (
    <Group fullWidth direction="vertical" gap={32} tabletGap={40}>
      <Heading variant="heading2">Rooms & Amenities</Heading>
      {offer.property.rooms.length > 0 && (
        <BedroomGrid>
          {roomsWithBeds.map((bedroom, index) => (
            <BedroomTile key={index}>
              <Group direction="horizontal" gap={4} verticalAlign="center">
                {take(
                  bedroom.beds.flatMap(bed => fillArray(bed.count, () => bed.type)),
                  3).map((bedType, bedIndex) =>
                  <React.Fragment key={bedIndex}>
                    {bedIndex < 2 && bedroomTypeToIcon(bedType)}
                    {/* At most we show 2 bed icons and after that we indicate there are more with a + */}
                    {bedIndex === 2 && <LinePlusIcon size="XS" />}
                  </React.Fragment>,
                )}
              </Group>
              <div>
                <BodyText variant="large" weight="bold" colour="neutral-two">
                  {bedroom.name} {index + 1}
                </BodyText>
                <BodyText variant="medium" colour="neutral-two">
                  {bedroom.beds.map(bed => pluralizeToString(bed.name, bed.count)).join(', ')}
                </BodyText>
              </div>
            </BedroomTile>
          ))}
        </BedroomGrid>
      )}
      {!!offer.property.highlights.length && <Group direction="vertical" gap={12}>
        <Heading variant="heading4">Highlights</Heading>
        <CardCarousel gap={12} pageSize={3}>
          {offer.property.highlights.map((highlight) => <HighlightTile gap={12} key={highlight.id}>
            <Image image={highlight.image} width={180} height={180} fit="center" />
            <div>
              <BodyText variant="large" weight="bold" colour="neutral-two">{highlight.title}</BodyText>
              <BodyText variant="medium" colour="neutral-two">{highlight.description}</BodyText>
            </div>
          </HighlightTile>,
          )}
        </CardCarousel>
      </Group>}
      {!!amenities.length && (
        <VerticalSpacer gap={20}>
          <Heading variant="heading4">Amenities</Heading>
          <AmenitiesGrid>
            {take(amenities, 10).map((amenity) => (<VillasPageAmenity key={amenity.description} amenity={amenity} />))}
          </AmenitiesGrid>
          {amenities.length > 10 && <TextButton kind="secondary" onClick={openAmenitiesModal}>
            View all {amenities.length} amenities
          </TextButton>}
        </VerticalSpacer>
      )}
      {vipServices.length > 0 && (
        <VerticalSpacer gap={8}>
          <Heading variant="heading4">
            Enjoy your stay with our <Heading variant="heading4" as="span" colour="exclusive">VIP services</Heading>
          </Heading>
          <ServicesGrid>
            {vipServices.map((vipService) => <ServiceItem key={vipService}>
              <SolidCheckCircleIcon colour="exclusive" />
              <BodyText variant="medium" weight="bold" colour="neutral-two">{vipService}</BodyText>
            </ServiceItem>)}
          </ServicesGrid>
        </VerticalSpacer>
      )}
    </Group>
  )
}

export default VillasPageRoomsAndAmenities
